import React, { useEffect, useRef, useState } from "react";
import { Row, Col, FormGroup } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { LabelText } from "../../../Utils/Input";
import InputField from "../../../Utils/InputField";
import CommonButton from "../../../Utils/CommonButton";
import { Loader } from "../../../Utils/Loader";

import { CiImageOn } from "react-icons/ci";
import { FaStar } from "react-icons/fa";

import { uploadPublicFile } from "../../../../slices/RedemptionOffer/ImageUpload/thunk";

const IMAGE_HOST = "https://api.sandbox.blu-ocean.net/cms-service";

const DEFAULT_CATEGORIES = [
  "Electronics",
  "Groceries",
  "Apparel",
  "Beverages",
  "Appliances",
  "Membership",
];

function BasicDetailsForm({ initialData = {}, onDataChange }) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [customCategory, setCustomCategory] = useState("");

  useEffect(() => {
    const apiCategory = initialData?.category?.trim();

    if (!apiCategory) {
      return;
    }

    setCategories((prev) => {
      if (prev.includes(apiCategory)) {
        return prev;
      }

      return [...prev, apiCategory];
    });
  }, [initialData?.category]);

  const fileInputRef = useRef(null);

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      offertitle: initialData?.offertitle || "",
      description: initialData?.description || "",
      category: initialData?.category || "",
      bannerimage: initialData?.bannerimage || null,
    },

    validationSchema: Yup.object({
      offertitle: Yup.string().required("Required"),
      category: Yup.string().required("Required"),
      bannerimage: Yup.mixed().required("Required"),
    }),

    onSubmit: () => {},
  });

  const { values, errors, touched, setFieldValue } = formik;

  useEffect(() => {
    onDataChange?.(values);
  }, [values, onDataChange]);

  const handleSaveCustomCategory = () => {
    const trimmed = customCategory.trim();

    if (!trimmed) {
      return;
    }

    if (!categories.includes(trimmed)) {
      setCategories((prev) => [...prev, trimmed]);
    }

    setFieldValue("category", trimmed);
    setCustomCategory("");
  };

  const getBannerPreviewUrl = (image) => {
    if (!image) {
      return null;
    }

    // New uploaded File
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }

    // String URL/path
    if (typeof image === "string") {
      if (image.startsWith("http://") || image.startsWith("https://")) {
        return image;
      }

      return `${IMAGE_HOST}${image.startsWith("/") ? "" : "/"}${image}`;
    }

    // Uploaded image object
    if (typeof image === "object") {
      const imagePath =
        image.url ||
        image.fileUrl ||
        image.imageUrl ||
        image.downloadUrl ||
        image.publicUrl ||
        image.location ||
        null;

      if (!imagePath) {
        return null;
      }

      if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
        return imagePath;
      }

      return `${IMAGE_HOST}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
    }

    return null;
  };

  const bannerPreviewUrl = getBannerPreviewUrl(values.bannerimage);

  const handleFileSelect = (file) => {
    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Only PNG and JPG images are allowed");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should not exceed 5MB");
      return;
    }

    dispatch(
      uploadPublicFile(file, setLoading, (response) => {
        const uploadedImage = response?.data?.[0];

        if (!uploadedImage?.id) {
          toast.error("Image upload failed");
          return;
        }
        setFieldValue("bannerimage", {
          id: uploadedImage.id,
          name: uploadedImage.name,
          url: uploadedImage.url,
        });
      }),
    );
  };

  return (
    <div className="card border-0 shadow-sm rounded-4">
      {loading && <Loader />}

      {/* HEADER */}
      <div className="card-body p-4 pb-3">
        <h4 className="fw-bold mb-1">Offer Information</h4>

        <p className="text-muted mb-0">
          How your offer appears to customers in the app.
        </p>
      </div>

      {/* FIELDS */}
      <div className="card-body border-top p-4">
        <Row className="gy-0">
          {/* OFFER TITLE */}
          <Col md={12}>
            <FormGroup>
              <LabelText
                displayText="Offer Title"
                className="form-label fw-semibold mb-2 text-danger"
                important="*"
              />

              <InputField
                name="offertitle"
                type="text"
                placeholder="e.g. 20% Off on Electronics"
                value={values.offertitle}
                handleChange={(e) =>
                  setFieldValue("offertitle", e.target.value)
                }
                invalid={touched.offertitle && errors.offertitle}
                className="rounded-3"
              />

              {touched.offertitle && errors.offertitle && (
                <div className="text-danger small mt-1">
                  {errors.offertitle}
                </div>
              )}
            </FormGroup>
          </Col>

          {/* DESCRIPTION */}
          <Col md={12}>
            <FormGroup>
              <LabelText
                displayText="Description"
                className="form-label fw-semibold mb-2"
              />

              <textarea
                name="description"
                rows={4}
                className="form-control rounded-3"
                placeholder="Briefly describe this offer to customers..."
                value={values.description}
                onChange={(e) => setFieldValue("description", e.target.value)}
              />
            </FormGroup>
          </Col>

          {/* CATEGORY */}
          <Col md={12}>
            <FormGroup className="mb-0">
              <LabelText
                displayText="Category"
                className="form-label fw-semibold mb-2 text-danger"
                important="*"
              />

              <div className="d-flex flex-wrap gap-2 mb-3">
                {categories.map((cat) => {
                  const isActive = values.category === cat;

                  return (
                    <button
                      key={cat}
                      type="button"
                      className="btn rounded-3"
                      style={{
                        backgroundColor: isActive
                          ? "var(--rv-primary-color)"
                          : "#fff",
                        borderColor: isActive
                          ? "var(--rv-primary-color)"
                          : "#dee2e6",
                        color: isActive ? "#fff" : "#495057",
                        fontWeight: isActive ? 600 : 400,
                      }}
                      onClick={() => setFieldValue("category", cat)}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>

              {touched.category && errors.category && (
                <div className="text-danger small mb-2">{errors.category}</div>
              )}

              <Row className="g-2">
                <Col md={8}>
                  <InputField
                    name="customcategory"
                    type="text"
                    placeholder="Add custom category..."
                    value={customCategory}
                    handleChange={(e) => setCustomCategory(e.target.value)}
                    className="rounded-3"
                  />
                </Col>

                <Col md={4}>
                  <CommonButton
                    outline
                    className="rounded-3 w-100 py-2 rv-primary-btn text-white"
                    style={{
                      color: "#ffffff",
                    }}
                    type="button"
                    buttonAction={handleSaveCustomCategory}
                    buttonText="+ Save"
                    disabled={!customCategory.trim()}
                  />
                </Col>
              </Row>
            </FormGroup>
          </Col>
        </Row>
      </div>

      {/* BANNER */}
      <div className="card-body border-top p-4">
        <h5 className="fw-bold mb-1">
          Offer Banner Image <span className="text-danger">*</span>
        </h5>

        <p className="text-muted mb-3">
          Shown at the top of your offer card in the app.
        </p>

        <div
          className="border rounded-4 d-flex justify-content-center align-items-center flex-column"
          style={{
            height: 220,
            borderStyle: "dashed",
            borderColor: "#dee2e6",
            background: "#fafafa",
            cursor: "pointer",
            overflow: "hidden",
            position: "relative",
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          {bannerPreviewUrl ? (
            <>
              {/* BLURRED IMAGE */}
              <img
                src={bannerPreviewUrl}
                alt="Banner Preview"
                style={{
                  position: "absolute",
                  inset: "-8px",
                  width: "calc(100% + 16px)",
                  height: "calc(100% + 16px)",
                  objectFit: "cover",
                  filter: "blur(0.5px)",
                  zIndex: 0,
                }}
              />

              {/* DARK OVERLAY */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0, 0, 0, 0.35)",
                  zIndex: 1,
                }}
              />

              {/* IMAGE NAME */}
              <div
                className="position-relative text-white text-center fw-semibold px-3"
                style={{
                  zIndex: 2,
                }}
              >
                {values.bannerimage?.name || "Banner image selected"}
              </div>

              <small
                className="position-relative text-white mt-1"
                style={{
                  zIndex: 2,
                }}
              >
                Click to change image
              </small>
            </>
          ) : (
            <>
              <div
                className="d-flex justify-content-center align-items-center rounded-3"
                style={{
                  width: 48,
                  height: 48,
                  background: "#FFE4D6",
                }}
              >
                <CiImageOn size={24} color="#f97316" />
              </div>

              <div className="fw-semibold mt-2">
                Click to{" "}
                <span
                  style={{
                    color: "#f97316",
                  }}
                >
                  browse
                </span>
              </div>

              <small className="text-muted">
                PNG, JPG up to 5MB · 16:9 recommended
              </small>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg"
            hidden
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
          />
        </div>
      </div>

      {/* TIP */}
      <div className="p-4 pt-0">
        <div
          className="rounded-4 p-3 d-flex align-items-start gap-2"
          style={{
            background: "#FFF3EE",
            border: "1px solid #FFD4C2",
          }}
        >
          <div
            className="d-flex justify-content-center align-items-center rounded"
            style={{
              width: 32,
              height: 28,
              background: "#FFE4D6",
            }}
          >
            <FaStar size={14} color="#f97316" />
          </div>

          <div>
            <strong
              style={{
                color: "#f97316",
              }}
            >
              Tip
            </strong>

            <div className="text-muted mt-1">
              Offers with a vibrant banner image get{" "}
              <strong
                style={{
                  color: "#000000",
                }}
              >
                3× more taps
              </strong>{" "}
              in the Elytra Rewards app.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BasicDetailsForm;
